<template>
  <v-dialog v-model="localModelValue" max-width="430px" persistent>
    <v-card class="dynamic-confirmation-modal">
      <div class="modal-inner-container">
        <div class="content-wrapper">
          <div class="title-icon-container">
            <div v-if="icon" class="modal-icon">
              <img
                v-if="typeof icon === 'string'"
                :src="icon"
                alt="modal-icon"
              />
              <component v-else :is="icon" width="32" height="32" />
            </div>
            <h3 class="modal-title font-bold text-title-large">{{ title }}</h3>
          </div>
          <p class="modal-message font-regular text-title-big">{{ message }}</p>
        </div>

        <div class="actions-wrapper">
          <ActionButton
            v-if="showCancelButton"
            variant="outline"
            size="medium"
            full-width
            class="modal-action-button back-button"
            @click="handleCancel"
          >
            {{ cancelText }}
          </ActionButton>
          <ActionButton
            v-if="showConfirmButton"
            variant="primary"
            size="medium"
            full-width
            :loading="loading"
            class="modal-action-button continue-button"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </ActionButton>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import ActionButton from "./ActionButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Confirmation",
  },
  message: {
    type: String,
    default: "Are you sure you want to proceed?",
  },
  confirmText: {
    type: String,
    default: "Confirm",
  },
  cancelText: {
    type: String,
    default: "Cancel",
  },
  icon: {
    type: [String, Object],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleConfirm = () => {
  emit("confirm");
};

const handleCancel = () => {
  emit("cancel");
  localModelValue.value = false;
};
</script>

<style scoped>
.dynamic-confirmation-modal {
  padding: 8px !important;
  border-radius: 12px !important;
  background-color: var(--white) !important;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1) !important;
  overflow: hidden;
}

.modal-inner-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  border: 2px solid var(--blue-light-2);
  border-radius: 8px;
  width: 100%;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.title-icon-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.modal-title {
  color: var(--black-body-text);
  margin: 0;
}

.modal-message {
  color: var(--black-body-text);
  margin: 0;
  letter-spacing: 0.15px;
}

.actions-wrapper {
  display: flex;
  gap: 8px;
  width: 100%;
}

/* Button overrides to match Figma */
.modal-action-button {
  height: 48px !important;
  margin-bottom: 0px;
}

.back-button {
  color: var(--neutral-900) !important;
  border-color: var(--neutral-900) !important;
}

.back-button:hover {
  background-color: rgba(64, 68, 75, 0.05) !important;
  border-color: var(--neutral-900) !important;
  color: var(--neutral-900) !important;
}

.continue-button {
  background-color: var(--blue-light-2) !important;
  border-color: var(--blue-light-2) !important;
}

.continue-button:hover {
  background-color: var(--blue-light-1) !important;
  border-color: var(--blue-light-1) !important;
}

@media (max-width: 480px) {
  .actions-wrapper {
    flex-direction: column;
  }
}
</style>
